import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { SimpleButton } from '../../../../components/buttons/SimpleButton';
import { TimesheetIntro } from '../../../../components/generic';
import { DateFilterField } from '../../../../components/tables/filterForms/DateFilterField';
import { FilterField, FilterForm } from '../../../../components/tables/filterForms/FilterForm';
import { FormSelect } from '../../../../components/tables/filterForms/FormSelect';
import SFCTable from '../../../../components/tables/SFCTable';
import { useDataContext } from '../../../../context';
import { useToast } from '../../../../context/ToastContext';
import { useListFiltersInUrl } from '../../../../hooks';
import { downloadIndex } from '../../../../services/api';
import { useGetTimesheetEntriesQuery } from '../../../../services/endpoints/finance/timesheets';
import { useGetInstructorsQuery } from '../../../../services/endpoints/people/user';
import { getCurrentUser } from '../../../../services/helper';
import { isAdmin, isGlobal } from '../../../../utils/consts';
import { getTimesheetColumns } from '../../../../utils/consts/columnDefs/timesheetColumns';
import { getFilterValue } from '../../../../utils/listFilterSearchParams';

export const TimesheetView = () => {
  const { timesheetId } = useParams();
  const { addToast } = useToast();
  const { locationOptions } = useDataContext();
  const currentUser = getCurrentUser();
  const isAdminUser = isAdmin(currentUser?.user_type_id!);
  const isGlobalUser = isGlobal(currentUser?.user_type_id!);
  const [filters, setFilters] = useListFiltersInUrl(
    { scope: 'all' },
    { omitScopeFromUrl: true }
  );
  const { data, error, isLoading, isFetching } = useGetTimesheetEntriesQuery(
    { timesheetId: +(timesheetId ?? 0), filters },
    { skip: !(timesheetId && filters) }
  );
  const {
    data: instructors,
    error: instructorsError,
    isLoading: instructorsLoading
  } = useGetInstructorsQuery(
      { data: { entries: 1000, orderBy: { field: 'first_name', order: 'asc' } } },
      { skip: !isAdminUser }
  );

  const instructorOptions =
    instructors?.results.map((i) => ({ value: i.id, label: i.full_name })) ?? [];

  const getFilterFormFields: () => FilterField[] = () => {
    const fields: FilterField[] = [
      {
        name: 'id',
        label: 'ID',
        type: 'number',
        value: getFilterValue(filters.data?.filterBy, 'id')
      },
      {
        name: 'instructor_id',
        label: 'Instructor',
        type: 'number',
        element: (
          <FormSelect
            name="instructor_id"
            label="Instructor"
            options={instructorOptions}
            loadingError={instructorsError ? 'Error loading instructors' : undefined}
            selected_Value={instructorOptions.find(
              (option) =>
                String(option.value) === getFilterValue(filters.data?.filterBy, 'instructor_id')
            )}
          />
        )
      },
      {
        name: 'location_id',
        label: 'Location',
        type: 'number',
        element: (
          <FormSelect
            name="location_id"
            label="Location"
            options={locationOptions}
            selected_Value={locationOptions.find(
              (option) =>
                String(option.value) === getFilterValue(filters.data?.filterBy, 'location_id')
            )}
          />
        )
      },
      {
        name: 'date_from',
        id: 'class_date',
        operator: 'greater_equal_than',
        element: (
          <DateFilterField
            name="date_from"
            label="Class Date Start"
          />
        ),
        value: getFilterValue(filters.data?.filterBy, 'class_date', 'greater_equal_than')
      },
      {
        name: 'date_to',
        id: 'class_date',
        operator: 'lesser_equal_than',
        element: (
          <DateFilterField
            name="date_to"
            label="Class Date End"
          />
        ),
        value: getFilterValue(filters.data?.filterBy, 'class_date', 'lesser_equal_than')
      }
    ];

    if (!isAdminUser) {
      fields.splice(1, 1);
    }

    if (!isGlobalUser) {
      fields.splice(2 - (!isAdminUser ? 1 : 0), 1);
    }

    return fields;
  };

  useEffect(() => {
    if (error && !isLoading) {
      addToast('Error while loading data', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, error, isLoading]);

  const setFilterBy = (filterBy: { operator: string; field: string; value: string }[]) => {
    setFilters((prev) => ({
      ...prev,
      data: {
        ...prev!.data,
        filterBy
      }
    }));
  };

  return (
    <div className="py-2">
      <SFCTable
        name={`Timesheet Entries`}
        columns={getTimesheetColumns(isAdminUser, isGlobalUser)}
        data={data?.results ?? []}
        count={data?.count}
        isLoading={isLoading}
        isFetching={isFetching}
        onFiltersChanged={setFilters}
        defaultScope={filters.scope ?? 'all'}
        filters={filters.data?.filterBy}
        indexDownloadPath={`timesheet-entries/${timesheetId}`}
        sidebars={[<TimesheetIntro />]}
        newItemEl={
          timesheetId !== undefined && isAdminUser ? (
            <SimpleButton
              onClick={() =>
                downloadIndex({ method: 'GET', format: 'xlsx', path: `timesheets/${timesheetId}` })
              }
              height={40}
              truncate={false}
              variant="primary"
            >
              Export
            </SimpleButton>
          ) : (
            <></>
          )
        }
        filterForm={
          <FilterForm
            fields={getFilterFormFields()}
            onApplyFilters={setFilterBy}
          />
        }
      />
    </div>
  );
};
